/*
 * Route-driven state container for a singular Revision.
 *
 * Fetches revision data via React Query (useCurrentRevision), provides it
 * to child components through RevisionContext, and polls automatically when
 * the revision is in an unsteady state.
 */

import { cloneElement, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { FunctionComponent, ReactElement } from 'react';

import { window } from '../../globals';
import { useCurrentRevision } from '../../hooks/api/revisions/useCurrentRevision';
import { getErrorMessage } from '../../ReactQuery/errors';
import NavigationStore from '../../redux/navigation';
import AppError from '../generic/AppError';
import NotFound from '../generic/NotFound';
import type { FullRevisionResponse } from '../../../shared/types/routes/revisions';

import { RevisionProvider } from './contexts/RevisionContext';
import { useRevisionScanIdQueryParam } from './hooks/useRevisionScanIdQueryParam';
import type { ProjectDashboardProject, ProjectDashboardRevision } from './projectDashboardTypes';

/**
 * Checks if an error is a 404 response.
 */
function isNotFoundError(error: unknown): boolean {
  return (error as Response)?.status === 404;
}

/**
 * Manages the `revisionScanId` query parameter.
 *
 * On initial load, sets the revisionScanId to the latestRevisionScanId if
 * no scan ID is present in the URL. Also validates that the current
 * revisionScanId exists in the revision's scan list.
 */
const RevisionScanIdContainer: FunctionComponent<{ revision: ProjectDashboardRevision }> = ({ children, revision }) => {
  const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();

  //  On the initial load, if the revisionScanId is not present, we set it to the latestRevisionScanId.
  useEffect(() => {
    if (!revisionScanId && revision.data?.latestRevisionScanId) {
      setRevisionScanId(revision.data.latestRevisionScanId);
    }
  }, [revision.data?.latestRevisionScanId, revisionScanId, setRevisionScanId]);

  // Validate that revisionScanId exists in revisionScans array.
  // This will unset the query param if the revisionScanId is not valid,
  // which causes the above hook to be re-run, setting the latestRevisionScanId if no revisionScanId is present.
  useEffect(() => {
    // Only validate if we have a revisionScanId, revision is not loading, and revision data is loaded
    if (revisionScanId && !revision.status.loading && revision.data?.revisionScans) {
      const isValid = revision.data.revisionScans.some(
        revisionScan => revisionScan.id === revisionScanId,
      );

      // If not valid, unset the query param
      if (!isValid) {
        setRevisionScanId(undefined);
      }
    }
    // The existing useEffect above will then set it to latestRevisionScanId
  }, [revision.data?.revisionScans, revision.status.loading, revisionScanId, setRevisionScanId]);

  return <>{children}</>;
};

/**
 * Container component for viewing a revision within the project dashboard.
 *
 * Fetches revision data via `useCurrentRevision` (React Query), adapts it to
 * the `ProjectDashboardRevision` context shape, and provides it to all child
 * components via `RevisionContext`.
 */
type Props = {
  children?: ReactElement;
  project?: ProjectDashboardProject;
  onProjectChange?: () => void;
};

const RevisionContainer: FunctionComponent<Props> = ({ children, project, onProjectChange }) => {
  const dispatch = useDispatch();
  const { data: revisionData, isLoading, error, isFullRevisionLoaded } = useCurrentRevision();
  const { revisionScanId } = useRevisionScanIdQueryParam();

  // Derive selectedRevisionScan from the revision data + the current revisionScanId query param.
  const selectedRevisionScan = useMemo(() => {
    if (!revisionData?.revisionScans || !revisionScanId) {
      return undefined;
    }
    return revisionData.revisionScans.find(rs => rs.id === revisionScanId);
  }, [revisionData?.revisionScans, revisionScanId]);

  // Build the context value in the ProjectDashboardRevision shape that consumers expect.
  // The API response data is compatible with ProjectDashboardRevision['data'] at runtime,
  // but the minimal endpoint's TypeScript type doesn't include all fields. We cast
  // through `unknown` to bridge the two type definitions.
  const revisionContextValue = useMemo<ProjectDashboardRevision>(() => ({
    data: revisionData
      ? { ...revisionData, selectedRevisionScan } as unknown as ProjectDashboardRevision['data']
      : undefined,
    status: {
      loading: isLoading,
      error: error ? getErrorMessage(error) : undefined,
      fullRevisionLoaded: isFullRevisionLoaded,
    },
    // isSteady is only computed by the full endpoint; on minimal paths default to false.
    isSteady: isFullRevisionLoaded ? (revisionData as FullRevisionResponse).isSteady : false,
  }), [revisionData, isLoading, error, isFullRevisionLoaded, selectedRevisionScan]);

  // Update navigation state when revision data changes.
  useEffect(() => {
    if (revisionData) {
      dispatch(NavigationStore.actions.setNavState({
        navProps: {
          revision: revisionData,
        },
      }));
    }
  }, [dispatch, revisionData]);

  if (error && isNotFoundError(error)) {
    return <NotFound />;
  }

  if (error) {
    return (
      <AppError
        heading="Error"
        text={getErrorMessage(error)}
        isLoggedIn={!!window.user}
        displayContactLink
      />
    );
  }

  return (
    <RevisionProvider value={revisionContextValue}>
      <RevisionScanIdContainer revision={revisionContextValue}>
        {children && cloneElement(children, {
          project,
          onProjectChange,
          revision: revisionContextValue,
        })}
      </RevisionScanIdContainer>
    </RevisionProvider>
  );
};

export default RevisionContainer;
